import React from 'react'
import Section from '@/components/Section'
import Container from '@/components/Container'
import Tooltip from '../../molecules/Tooltip'
import TaxonomyHeader from '@/components/TaxonomyHeader'
import TaxonomyHeading from '@/components/TaxonomyHeading'
import CenterPrimaryFocusDisclaimer from './CenterPrimaryFocusDisclaimer'
import { getCoOccurringDisorderLabel } from '@/domain/primaryFocus/useCases/getCoOccurringDisorderLabel'
import { isNonEmptyArray } from '@/utils/checks'
import {
  CurrencyDollar,
  GeoAltFill,
  ChatFill,
  FlagFill,
  PeopleFill,
  PersonLinesFill,
  ShieldFillCheck
} from 'react-bootstrap-icons'
import ResponsivePopover from '@/components/molecules/ResponsivePopover/ResponsivePopover'
import LinkButton from '@/components/atoms/LinkButton/LinkButton.tsx'
import IconChevronDownXs from '@/components/icons/IconChevronDownXs/IconChevronDownXs.tsx'

const icon =
  'https://res.cloudinary.com/rehabpath/image/upload/v1641592648/Wellness_center.svg'
const crossIcon =
  'https://res.cloudinary.com/rehabpath/image/upload/icon-insurance.svg'
const starIcon =
  'https://res.cloudinary.com/rehabpath/image/upload/v1641447567/trophy.svg'

const Overview = ({ data, cursor }) => {
  const {
    occupancy,
    founded,
    price_length,
    languages,
    acceptsInsurance,
    accredidation,
    who_we_treat,
    address,
    primaryFocus,
    what_we_treat,
    _what_we_treat,
    price_label,
    length_label
  } = data

  const coOccurringDisorder = getCoOccurringDisorderLabel({
    primaryFocus,
    _what_we_treat,
    what_we_treat
  })

  const whoWeTreat = who_we_treat.filter((tax) => tax?.title?.length > 0)

  return (
    <div className="pt-8">
      <Section>
        <Container>
          <TaxonomyHeader>
            <img
              src={icon}
              className="highlight-icon"
            />
            <TaxonomyHeading tag="h3">Center Overview</TaxonomyHeading>
          </TaxonomyHeader>

          <div className="mt-5 space-y-5">
            <div className="flex flex-col items-start justify-between gap-5 md:flex-row md:gap-10">
              {founded && (
                <div className="flex items-start gap-4 self-stretch md:w-1/3 md:gap-6">
                  <FlagFill
                    size="22"
                    className="mt-1 w-7 shrink-0 grow-0 text-gray-600"
                  />
                  <div>
                    <div className="text-lg font-medium text-gray-1100">
                      Founded
                    </div>
                    <div className="text-sm font-light text-gray-1100">
                      {founded}
                    </div>
                  </div>
                </div>
              )}
              {occupancy && (
                <div className="flex items-start gap-4 self-stretch md:w-1/3 md:gap-6">
                  <PeopleFill
                    size="24"
                    className="mt-1 w-7 shrink-0 grow-0 text-gray-600"
                  />
                  <div>
                    <div className="text-lg font-medium text-gray-1100">
                      Occupancy
                    </div>
                    <div className="text-sm font-light text-gray-1100">
                      {occupancy}
                    </div>
                  </div>
                </div>
              )}
              {acceptsInsurance && (
                <div className="flex items-start gap-4 self-stretch md:w-1/3 md:gap-6">
                  <ShieldFillCheck
                    size="24"
                    className="mt-1  w-7 shrink-0 grow-0 text-gray-600"
                  />
                  <div>
                    <div className="text-lg font-medium text-gray-1100">
                      Insurance
                    </div>
                    <div className="text-sm font-light text-gray-1100">
                      Accepted
                    </div>
                  </div>
                </div>
              )}
              {isNonEmptyArray(languages) && (
                <div className="flex items-start gap-4 self-stretch md:w-1/3 md:gap-6">
                  <ChatFill
                    size="24"
                    className="w-7 shrink-0 grow-0 text-gray-600"
                  />
                  <div>
                    <div className="text-lg font-medium text-gray-1100">
                      Languages
                    </div>
                    {languages.map((item) => (
                      <div
                        className="text-sm font-light text-gray-1100"
                        key={item}
                      >
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col items-start justify-between gap-5 md:flex-row md:gap-10">
              {(isNonEmptyArray(price_length) ||
                (price_label && length_label)) && (
                <div className="order-4 w-full md:order-1 md:w-1/3 md:gap-6">
                  <div className="relative">
                    <div
                      id="center-price"
                      className="invisible absolute top-[-114px] h-0 md:top-[-140px] lg:top-[-154px]"
                    ></div>
                  </div>
                  <div className="flex w-full items-start gap-4 self-stretch ">
                    <CurrencyDollar
                      size="24"
                      className="w-7 shrink-0 grow-0 text-gray-600"
                    />

                    <div className="flex flex-col gap-2 md:overflow-hidden">
                      <div className="text-lg font-medium text-gray-1100">
                        {acceptsInsurance
                          ? 'Estimated Cash Pay Rate'
                          : 'Price & Length'}
                      </div>
                      <div className="flex w-full flex-col items-start gap-4">
                        {price_length.map((item) => (
                          <div
                            className="flex flex-col items-start justify-between gap-2"
                            key={item.price}
                          >
                            <div className="self-stretch text-sm font-light text-gray-1100">
                              {item.length}
                            </div>

                            <div className="text-sm font-medium text-gray-1100">
                              {item.price}
                            </div>
                          </div>
                        ))}
                        {!isNonEmptyArray(price_length) &&
                          price_label &&
                          length_label && (
                            <div className="flex flex-col items-start justify-between">
                              <div className="text-sm font-medium text-gray-1100">
                                {price_label} - {length_label}
                              </div>
                            </div>
                          )}
                      </div>
                      <div>
                        <ResponsivePopover
                          header={
                            acceptsInsurance
                              ? 'Estimated Cash Pay Rate'
                              : 'Estimated Center Costs'
                          }
                          description={
                            acceptsInsurance ? (
                              <div>
                                If you are <strong>not</strong> using insurance,
                                the cost listed above, {price_label?.trim()}, is
                                an estimate of the cash pay rate. Center pricing
                                can vary based on program and length of stay.
                                Contact the center for more information.
                                Recovery.com strives for price transparency so
                                you can make an informed decision.
                              </div>
                            ) : (
                              `The cost listed here (${price_label?.trim()}), is an estimate of program cost. Center price can vary based on program and length of stay. Contact the center for more information. Recovery.com strives for price transparency so you can make an informed decision.`
                            )
                          }
                        >
                          <LinkButton
                            label="Learn More"
                            iconElement={
                              <IconChevronDownXs
                                strokeWidth="0.2"
                                className=" text-black"
                              />
                            }
                          />
                        </ResponsivePopover>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {isNonEmptyArray(whoWeTreat) && (
                <div className="order-1 flex grow items-start gap-4 md:order-2 md:w-1/3 md:gap-6">
                  <PersonLinesFill
                    size="26"
                    className="w-7 shrink-0 grow-0 text-gray-600"
                  />
                  <div className="flex grow flex-col gap-2">
                    <div className="text-lg font-medium text-gray-1100">
                      Who We Treat
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {whoWeTreat.map((item, i) =>
                        item?.tooltip_description ? (
                          <div
                            className="bg-gray-100 px-2 py-1 text-sm leading-6"
                            key={item.title}
                          >
                            <Tooltip
                              cursor={cursor}
                              noListen
                              className="tooltip"
                              treatmentButton
                              borderBottom={'1px dotted black'}
                              tooltipTitle={item?.title}
                              triggerText={item?.title}
                              footerText={item?.title}
                              footerLink={item?.tooltip_link}
                              tooltipBodyText={item?.tooltip_description}
                              addPadding
                              key={item.title}
                            />
                          </div>
                        ) : (
                          <div
                            className="bg-gray-100 px-2 py-1"
                            key={item.title}
                          >
                            {item.title}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </div>
              )}
              {accredidation && (
                <div className="order-3 flex grow items-start gap-4 self-stretch md:order-3 md:w-1/3 md:gap-6">
                  <div className="w-[30px] shrink-0 grow-0">
                    <img src={starIcon} />
                  </div>
                  <div>
                    <div className="text-lg font-medium text-gray-1100">
                      Accreditation
                    </div>
                    <div className="text-sm font-light text-gray-1100">
                      {accredidation}
                    </div>
                  </div>
                </div>
              )}
              <div className="order-2 flex items-start gap-4 md:order-4 md:w-1/3 md:gap-6">
                <GeoAltFill
                  size="24"
                  className="mt-1 w-7 shrink-0 grow-0 text-gray-600"
                />

                <div>
                  <div className="text-lg font-medium text-gray-1100">
                    Address
                  </div>
                  <div className="text-sm font-light text-gray-1100">
                    {address}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {primaryFocus && (
            <CenterPrimaryFocusDisclaimer
              primaryFocus={primaryFocus}
              coOccurringDisorder={coOccurringDisorder}
              className="mt-10"
            />
          )}
        </Container>
      </Section>
    </div>
  )
}

export default Overview
